import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import NestPage from '../pages/NestPage';
import GardenPage from '../pages/GardenPage';
import AdventurePage from '../pages/AdventurePage';
import TreasurePage from '../pages/TreasurePage';
import TreehousePage from '../pages/TreehousePage';
import FlipBookReader from './FlipBookReader';
import { redirectToOldPlatform } from '../utils/oldPlatformRedirect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token, logout, refreshUser, updateUser } = useAuth();
  const { theme } = useTheme();
  const [activeScreen, setActiveScreen] = useState('nest');
  const [readerBookId, setReaderBookId] = useState(null);
  const [stats, setStats] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showProfileAvatarPicker, setShowProfileAvatarPicker] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const userMenuRef = useRef(null);
  const avatarOptions = [
    { emoji: '🦊', name: 'Fox' },
    { emoji: '🦉', name: 'Owl' },
    { emoji: '🐻', name: 'Bear' },
    { emoji: '🐰', name: 'Rabbit' },
    { emoji: '🐼', name: 'Panda' },
    { emoji: '🦁', name: 'Lion' },
    { emoji: '🐸', name: 'Frog' },
    { emoji: '🐧', name: 'Penguin' },
    { emoji: '🦜', name: 'Parrot' },
    { emoji: '🦚', name: 'Peacock' },
    { emoji: '🦔', name: 'Hedgehog' },
    { emoji: '🐨', name: 'Koala' },
    { emoji: '🦌', name: 'Deer' },
    { emoji: '🦆', name: 'Duck' },
    { emoji: '🐦', name: 'Bluebird' },
  ];

  useEffect(() => {
    fetchStats();
  }, [activeScreen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [userMenuOpen]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const navItems = [
    { id: 'nest', label: 'Nest', icon: 'nest' },
    { id: 'garden', label: 'Garden', icon: 'garden' },
    { id: 'adventure', label: 'Adventure', icon: 'adventure' },
    { id: 'treasure', label: 'Treasure', icon: 'treasure' },
    { id: 'treehouse', label: 'Treehouse', icon: 'treehouse' }
  ];

  const openReader = (bookId) => setReaderBookId(bookId);
  
  const closeReader = () => {
    setReaderBookId(null);
    refreshUser();
    fetchStats();
    setRefreshKey(prev => prev + 1); // Force pages to refresh
  };

  const saveProfileAvatar = async () => {
    if (!selectedAvatar || selectedAvatar === user?.avatar || savingAvatar) return;
    setSavingAvatar(true);
    try {
      const res = await fetch(`${API}/user/avatar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ avatar: selectedAvatar }),
      });
      if (!res.ok) throw new Error('Failed to save avatar');
      updateUser({ avatar: selectedAvatar });
      setShowProfileAvatarPicker(false);
    } catch (err) {
      console.error('Failed to update avatar:', err);
      alert('Could not update avatar. Please try again.');
    } finally {
      setSavingAvatar(false);
    }
  };

  // Word of the Day - changes daily based on date
  const getDailyWord = () => {
    const words = [
      { word: "Luminous", type: "ADJECTIVE", emoji: "✨", definition: "Full of bright, glowing light — like stars or fireflies shining in the dark.", example: "The <em>luminous</em> moon lit up the whole forest path so the animals could find their way home." },
      { word: "Courage", type: "NOUN", emoji: "🦁", definition: "The ability to do something that frightens you; bravery in the face of fear.", example: "It took <em>courage</em> for the little bird to jump from the nest and learn to fly." },
      { word: "Serene", type: "ADJECTIVE", emoji: "🌊", definition: "Calm, peaceful, and untroubled — like a quiet morning by the lake.", example: "The <em>serene</em> garden made everyone feel relaxed and happy." },
      { word: "Curious", type: "ADJECTIVE", emoji: "🔍", definition: "Eager to know or learn something; full of questions and wonder.", example: "The <em>curious</em> kitten explored every corner of the new house." },
      { word: "Whisper", type: "VERB", emoji: "🤫", definition: "To speak very softly or quietly, often sharing a secret.", example: "She would <em>whisper</em> goodnight to her teddy bear before falling asleep." },
      { word: "Harmony", type: "NOUN", emoji: "🎵", definition: "When different things come together in a pleasant, balanced way.", example: "The forest animals lived in perfect <em>harmony</em>, each helping the other." },
      { word: "Grateful", type: "ADJECTIVE", emoji: "🙏", definition: "Feeling or showing thanks and appreciation for kindness.", example: "She felt <em>grateful</em> for her friend's help with the heavy books." }
    ];

    // Use day of year to pick a word (changes daily)
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now - start;
    const oneDay = 1000 * 60 * 60 * 24;
    const dayOfYear = Math.floor(diff / oneDay);
    
    return words[dayOfYear % words.length];
  };

  const dailyWord = getDailyWord();

  const renderIcon = (iconType, isActive) => {
    const color = isActive ? 'white' : 'currentColor';
    switch (iconType) {
      case 'nest':
        return (
          <svg viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 19C6 14 26 14 26 19L25 24C24 26 8 26 7 24Z"></path>
            <path d="M8 17C10 15 13 16 16 15C19 14 22 15 24 17"></path>
            <ellipse cx="12" cy="19" rx="3" ry="2.2" fill="rgba(255,200,80,.5)" strokeWidth="1.5"></ellipse>
            <ellipse cx="20" cy="19" rx="3" ry="2.2" fill="rgba(140,220,160,.5)" strokeWidth="1.5"></ellipse>
          </svg>
        );
      case 'garden':
        return (
          <svg viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 9L16 11L28 9L28 25L16 23L4 25Z"></path>
            <line x1="16" y1="11" x2="16" y2="23"></line>
            <path d="M22 6L23 8.5L25.5 9.5L23 10.5L22 13L21 10.5L18.5 9.5L21 8.5Z" fill={color} opacity=".8"></path>
          </svg>
        );
      case 'adventure':
        return (
          <svg viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="16" y1="3" x2="16" y2="7"></line>
            <path d="M12 7C12 7 16 6 20 7"></path>
            <rect x="9" y="7" width="14" height="18" rx="4"></rect>
            <rect x="12" y="10" width="8" height="12" rx="2" fill="rgba(255,210,60,.35)" stroke="none"></rect>
          </svg>
        );
      case 'treasure':
        return (
          <svg viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="16" width="24" height="12" rx="2"></rect>
            <path d="M4 16C4 11 28 11 28 16"></path>
            <rect x="13" y="20" width="6" height="5" rx="1"></rect>
            <circle cx="10" cy="22" r="1.5" fill="rgba(255,160,50,.9)" stroke="none"></circle>
          </svg>
        );
      case 'treehouse':
        return (
          <svg viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 28L14 20L18 20L18 28"></path>
            <line x1="10" y1="20" x2="22" y2="20"></line>
            <path d="M11 20L11 13L21 13L21 20"></path>
            <path d="M9 13L16 6L23 13"></path>
          </svg>
        );
      default:
        return null;
    }
  };

  const renderScreen = () => {
    switch (activeScreen) {
      case 'nest':
        return <NestPage key={refreshKey} onOpenReader={openReader} onNavigate={setActiveScreen} />;
      case 'garden':
        return <GardenPage key={refreshKey} onOpenReader={openReader} />;
      case 'adventure':
        return <AdventurePage key={refreshKey} />;
      case 'treasure':
        return <TreasurePage key={refreshKey} />;
      case 'treehouse':
        return <TreehousePage />;
      default:
        return <NestPage key={refreshKey} onOpenReader={openReader} onNavigate={setActiveScreen} />;
    }
  };

  return (
    <div className="shell">
      {/* ═══════════ LEFT SIDEBAR ═══════════ */}
      <aside id="app-sidebar">
        <div className="sidebar-logo">
          <svg viewBox="0 0 24 24">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
          </svg>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveScreen(item.id)}
              data-testid={`nav-${item.id}`}
              className={`nav-tab ${activeScreen === item.id ? 'active' : ''}`}
            >
              {renderIcon(item.icon, activeScreen === item.id)}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-bottom sidebar-user-wrap" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setUserMenuOpen((open) => !open)}
            className="sidebar-user hover:opacity-80 transition-all cursor-pointer"
            style={{ border: 'none', background: 'transparent', padding: 0, width: '100%' }}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            data-testid="sidebar-user-menu-trigger"
          >
            <div className="sidebar-user-avatar">{user?.avatar || '🦊'}</div>
            <span className="sidebar-user-name">{user?.name?.split(' ')[0] || 'Student'}</span>
          </button>
          {userMenuOpen && (
            <div className="sidebar-user-menu" role="menu" data-testid="sidebar-user-menu">
              <button
                type="button"
                role="menuitem"
                className="sidebar-user-menu-item sidebar-user-menu-item-signout"
                onClick={() => {
                  setUserMenuOpen(false);
                  logout();
                }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ═══════════ MAIN CONTENT ═══════════ */}
      <main className="main-content">
        <div className="content-wrap">
          {renderScreen()}
        </div>
      </main>

      {/* ═══════════ RIGHT PANEL ═══════════ */}
      <aside id="right-panel">
        {/* My Treehouse Header - Clickable to navigate to Treehouse page */}
        <button 
          onClick={() => {
            setSelectedAvatar(user?.avatar || '🦊');
            setShowProfileAvatarPicker(true);
          }}
          className="rp-treehouse-header hover:opacity-80 transition-all cursor-pointer w-full text-left"
          style={{ border: 'none', background: 'transparent' }}
          data-testid="open-profile-avatar-picker"
        >
          <div className="rp-treehouse-icon">{user?.avatar || '🦊'}</div>
          <div className="rp-treehouse-info">
            <div className="rp-treehouse-title">My Treehouse</div>
            <div className="rp-treehouse-sub">{user?.name || 'Reader'} · {user?.grade || 'Grade 2'}</div>
          </div>
        </button>

        {/* Stats Grid */}
        <div className="rp-stats">
          <div className="rp-stat">
            <div className="rp-stat-value">{stats?.books_read || 0}</div>
            <div className="rp-stat-label">Books Read</div>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-value">{stats?.leaf_points || 0}</div>
            <div className="rp-stat-label">Leaf Points</div>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-value">{stats?.streak_days || 1}</div>
            <div className="rp-stat-label">Day Streak</div>
          </div>
          <div className="rp-stat">
            <div className="rp-stat-value">{stats?.words_saved || 0}</div>
            <div className="rp-stat-label">Words</div>
          </div>
        </div>

        {/* Word of the Day */}
        <div className="rp-word-day">
          <div className="rp-word-header">
            <span className="rp-word-icon">⭐</span>
            <div>
              <div className="rp-word-label">Word of the Day</div>
              <div className="rp-word-sub">Learn one new word!</div>
            </div>
          </div>
          <div className="rp-word-content">
            <div className="rp-word-title">{dailyWord.word}</div>
            <div className="rp-word-type">{dailyWord.type}</div>
            <div className="rp-word-emoji">{dailyWord.emoji}</div>
            <div className="rp-word-def">{dailyWord.definition}</div>
            <div className="rp-word-example" dangerouslySetInnerHTML={{ __html: dailyWord.example }}></div>
          </div>
        </div>

        <div className="rp-actions">
          <button 
            onClick={() => redirectToOldPlatform()}
            className="rp-btn-secondary"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              marginBottom: '12px',
              fontWeight: '600',
              border: 'none'
            }}
          >
            ← Switch to Old Platform
          </button>
        </div>
      </aside>

      {/* ═══════════ BOTTOM NAV (Mobile) ═══════════ */}
      <nav id="bottom-nav">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveScreen(item.id)}
            data-testid={`mobile-nav-${item.id}`}
            className={`nav-tab ${activeScreen === item.id ? 'active' : ''}`}
            title={item.label}
          >
            {renderIcon(item.icon, activeScreen === item.id)}
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      {/* ═══════════ BOOK READER ═══════════ */}
      {readerBookId && (
        <FlipBookReader bookId={readerBookId} onClose={closeReader} />
      )}

      {showProfileAvatarPicker && (
        <div className="fixed inset-0 z-[12000] bg-black/45 flex items-center justify-center p-4">
          <div
            className="w-full max-w-[640px] rounded-3xl border p-6 sm:p-7"
            style={{ background: '#FDF8EE', borderColor: '#E4EDE5' }}
          >
            <div className="text-center mb-4">
              <h2 className="text-3xl font-bold text-[#1B4D32]" style={{ fontFamily: 'Fredoka' }}>
                Choose Your Animal Friend <span role="img" aria-label="sparkle">🌟</span>
              </h2>
              <p className="mt-2 text-[#4C7060] text-lg">
                Your companion travels with you on every reading adventure.
              </p>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {avatarOptions.map((a) => {
                const active = selectedAvatar === a.emoji;
                return (
                  <button
                    key={a.emoji}
                    type="button"
                    onClick={() => setSelectedAvatar(a.emoji)}
                    className={`rounded-2xl p-3 text-center border-2 transition-all ${
                      active ? 'scale-[1.02] shadow-sm' : 'hover:scale-[1.01]'
                    }`}
                    style={{
                      background: '#EFE7D3',
                      borderColor: active ? '#52B788' : '#EFE7D3',
                    }}
                    data-testid={`profile-avatar-${a.name.toLowerCase()}`}
                  >
                    <div className="text-3xl leading-none mb-2">{a.emoji}</div>
                    <div className="text-sm font-semibold text-[#2F5D4B]">{a.name}</div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setShowProfileAvatarPicker(false)}
                className="px-[19px] py-[11px] rounded-xl font-bold text-[95%]"
                style={{ background: '#D9E8E1', color: '#2F5D4B' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveProfileAvatar}
                disabled={savingAvatar || !selectedAvatar}
                className="flex-1 py-[15px] rounded-[22px] text-white text-[28.5px] font-extrabold disabled:opacity-60"
                style={{ background: '#2F7A57', fontFamily: 'Fredoka' }}
                data-testid="save-profile-avatar"
              >
                {savingAvatar ? 'Saving...' : 'Save My Animal Friend'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
